import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

export const TermsOfServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="terms-of-service-page" className="min-h-screen bg-background">
      <SEO
        title="Terms of Service"
        description="The terms governing use of TurfPulse — account rules, subscription terms, acceptable use, EPA/pesticide disclaimers, and liability limits."
        path="/terms"
      />
      {user && <DepartmentNav />}

      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-muted-foreground">Last updated: December 15, 2025</p>
        </div>

        {/* Introduction */}
        <Card>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none pt-6 text-foreground">
            <p className="text-lg">
              Welcome to TurfPulse. These Terms of Service ("Terms") govern your use of the TurfPulse website 
              and services located at getturfpulse.com (the "Service"). By accessing or using our Service, 
              you agree to be bound by these Terms.
            </p>
            <p>
              Please read these Terms carefully before using our Service. If you do not agree with any part 
              of these Terms, you may not access the Service.
            </p>
          </CardContent>
        </Card>

        {/* Acceptance of Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">1. Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              By creating an account or using TurfPulse, you represent that you are at least 18 years old 
              and have the legal capacity to enter into these Terms. If you are using the Service on behalf 
              of an organization, you represent that you have authority to bind that organization to these Terms.
            </p>
          </CardContent>
        </Card>

        {/* Description of Service */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">2. Description of Service</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              TurfPulse provides lawn care management tools including, but not limited to:
            </p>
            <ul>
              <li>Growing Degree Day (GDD) tracking and calculations</li>
              <li>Lawn treatment application logging and scheduling</li>
              <li>Property and zone management</li>
              <li>Equipment maintenance tracking</li>
              <li>Seasonal lawn care calendars</li>
              <li>Spreader settings library</li>
              <li>Product recommendations and affiliate links</li>
              <li>Partners directory</li>
            </ul>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time 
              without prior notice.
            </p>
          </CardContent>
        </Card>

        {/* User Accounts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">3. User Accounts</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>To access certain features, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Promptly update any changes to your information</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="mt-4">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in 
              fraudulent, abusive, or illegal activity.
            </p>
          </CardContent>
        </Card>

        {/* Subscriptions and Payments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">4. Subscriptions and Payments</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              TurfPulse offers both free and premium subscription tiers. By subscribing to a paid plan, you agree to:
            </p>
            <ul>
              <li>Pay all applicable fees as described at the time of purchase</li>
              <li>Provide valid payment information</li>
              <li>Authorize automatic renewal at the current rate unless canceled</li>
            </ul>

            <h4 className="font-semibold mt-6">Cancellation and Refunds</h4>
            <p>
              You may cancel your subscription at any time. Upon cancellation, you will retain access to 
              premium features until the end of your current billing period. Refunds are handled on a 
              case-by-case basis; please contact support@getturfpulse.com for refund requests.
            </p>

            <h4 className="font-semibold mt-6">Price Changes</h4>
            <p>
              We reserve the right to change subscription pricing. Existing subscribers will be notified 
              at least 30 days before any price changes take effect.
            </p>
          </CardContent>
        </Card>

        {/* Acceptable Use */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">5. Acceptable Use</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Upload malicious code, viruses, or harmful data</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Scrape, harvest, or collect data without permission</li>
              <li>Spam, harass, or abuse other users</li>
              <li>Interfere with the proper functioning of the Service</li>
              <li>Submit false or misleading information</li>
            </ul>
          </CardContent>
        </Card>

        {/* User Content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">6. User Content</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              You may submit content to TurfPulse, including spreader settings, reviews, and other 
              user-generated content. By submitting content, you:
            </p>
            <ul>
              <li>Grant us a non-exclusive, royalty-free license to use, display, and distribute your content</li>
              <li>Represent that you own or have rights to the content you submit</li>
              <li>Agree that your content does not violate any third-party rights</li>
            </ul>
            <p className="mt-4">
              We reserve the right to remove any user content that violates these Terms or is otherwise objectionable.
            </p>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">7. Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              The Service and its original content (excluding user-generated content), features, and 
              functionality are owned by TurfPulse and are protected by copyright, trademark, and other 
              intellectual property laws.
            </p>
            <p>
              The TurfPulse name, logo, and all related names, logos, product and service names, designs, 
              and slogans are trademarks of TurfPulse. You may not use these marks without our prior 
              written permission.
            </p>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">8. Disclaimer of Warranties</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p className="font-semibold">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED.
            </p>
            <p>We do not warrant that:</p>
            <ul>
              <li>The Service will be uninterrupted, secure, or error-free</li>
              <li>Results from using the Service will be accurate or reliable</li>
              <li>GDD calculations or product recommendations will be suitable for your specific lawn conditions</li>
              <li>Weather data from third-party sources will be accurate</li>
            </ul>
            <p className="mt-4">
              Lawn care decisions should be made based on your own judgment, local conditions, and 
              consultation with lawn care professionals where appropriate. TurfPulse is an informational 
              tool and should not be relied upon as the sole basis for lawn treatment decisions.
            </p>
          </CardContent>
        </Card>

        {/* EPA & Pesticide Compliance Disclaimer */}
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="text-xl text-amber-700 dark:text-amber-400">9. EPA & Pesticide Compliance Disclaimer</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p className="font-semibold">
              IMPORTANT: TurfPulse is NOT a substitute for professional advice or regulatory compliance.
            </p>
            <ul>
              <li><strong>Product Labels:</strong> Always read and follow the product label. The label is the law. Application rates, timing, safety precautions, and restricted-use requirements vary by product.</li>
              <li><strong>EPA Compliance:</strong> TurfPulse assists with record-keeping but does NOT guarantee EPA compliance. You are solely responsible for compliance with all local, state, and federal regulations regarding pesticide application.</li>
              <li><strong>Weather Data:</strong> Weather data (temperature, humidity, wind speed/direction) is provided by third-party sources for informational purposes. Accuracy is not guaranteed. Do not rely solely on this data for drift mitigation decisions.</li>
              <li><strong>GDD Calculations:</strong> Growing Degree Day (GDD) values are estimates based on historical weather data and may vary ±3% from other sources due to weather station differences.</li>
              <li><strong>Product Recommendations:</strong> TurfPulse does not manufacture, stock, or sell any products directly. Product recommendations link to third-party retailers (primarily Amazon) via affiliate partnerships, meaning TurfPulse may earn a commission when you purchase through these links, at no additional cost to you. Product mentions are for informational purposes only. TurfPulse is not responsible for product performance, availability, or any damages resulting from product use or misuse.</li>
              <li><strong>Licensing:</strong> Some products mentioned may require a pesticide applicator license. Check your state's requirements before purchasing or applying restricted-use products.</li>
            </ul>
            <p className="text-amber-700 dark:text-amber-400 font-medium">
              By using TurfPulse, you acknowledge that you are solely responsible for all lawn care and pesticide application decisions and compliance with applicable laws.
            </p>
          </CardContent>
        </Card>

        {/* Limitation of Liability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">10. Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TURFPULSE SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Loss of profits, data, or goodwill</li>
              <li>Service interruption or computer damage</li>
              <li>Damage to lawn, property, or equipment resulting from use of the Service</li>
              <li>Cost of substitute goods or services</li>
            </ul>
            <p className="mt-4">
              Our total liability shall not exceed the amount you paid us in the twelve (12) months 
              preceding the claim, or $100, whichever is greater.
            </p>
          </CardContent>
        </Card>

        {/* Indemnification */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">11. Indemnification</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              You agree to indemnify, defend, and hold harmless TurfPulse, its officers, directors, 
              employees, agents, and affiliates from any claims, damages, losses, liabilities, and 
              expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you submit to the Service</li>
            </ul>
          </CardContent>
        </Card>

        {/* Third-Party Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">12. Third-Party Links and Services</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              The Service may contain links to third-party websites or services, including Amazon 
              affiliate links. We are not responsible for:
            </p>
            <ul>
              <li>The content, accuracy, or practices of third-party sites</li>
              <li>Products or services purchased through third-party links</li>
              <li>Privacy practices of third-party services</li>
            </ul>
            <p className="mt-4">
              Your interactions with third-party services are governed by their respective terms and policies.
            </p>
          </CardContent>
        </Card>

        {/* Affiliate Disclosure */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">13. Affiliate Disclosure</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              TurfPulse is a participant in the Amazon Services LLC Associates Program, an affiliate 
              advertising program. Product links marked as "paid link" or "affiliate link" may earn 
              us a commission at no additional cost to you. For full details, see our{' '}
              <a href="/disclosure" className="text-primary hover:underline">Affiliate Disclosure</a>.
            </p>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">14. Governing Law</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State 
              of Texas, United States, without regard to its conflict of law provisions. Any disputes 
              arising from these Terms or the Service shall be resolved in the state or federal courts 
              located in Travis County, Texas.
            </p>
          </CardContent>
        </Card>

        {/* Dispute Resolution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">15. Dispute Resolution</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              Before filing a legal claim, you agree to attempt to resolve any dispute informally by 
              contacting us at support@getturfpulse.com. We will attempt to resolve the dispute 
              within 30 days.
            </p>
            <p>
              If we cannot resolve the dispute informally, you and TurfPulse agree to resolve any 
              claims through binding arbitration on an individual basis, except where prohibited by law.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">16. Changes to Terms</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material 
              changes by posting the new Terms on this page and updating the "Last updated" date. 
              Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </CardContent>
        </Card>

        {/* Severability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">17. Severability</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision 
              will be limited or eliminated to the minimum extent necessary, and the remaining 
              provisions will remain in full force and effect.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">18. Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>If you have any questions about these Terms, please contact us:</p>
            <ul>
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:support@getturfpulse.com" className="text-primary hover:underline">
                  support@getturfpulse.com
                </a>
              </li>
              <li>
                <strong>Privacy Inquiries:</strong>{' '}
                <a href="mailto:privacy@getturfpulse.com" className="text-primary hover:underline">
                  privacy@getturfpulse.com
                </a>
              </li>
              <li>
                <strong>Mailing Address:</strong><br />
                TurfPulse<br />
                110 N IH 35 Ste 315 #3365<br />
                Round Rock, Texas 78681
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center pt-4">
          <Button variant="outline" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};
